/**
 * Shared select lists (form_option_lists, migration 00522): one named set of
 * values that any single_select / multi_select field can point at by id
 * (FormField.optionListId) instead of carrying its own options.
 *
 * Lists are read at render time, so editing a list updates every form that
 * uses it without a republish. A value is retired, never deleted: a retired
 * value is hidden from new selections but still resolves (label and validity)
 * wherever it is already saved, so submissions and drafts keep reading right.
 *
 * Everything here is pure: the data service validates writes with it, the
 * renderer resolves options with it, and the Select Lists page parses pastes
 * with it.
 */

import type { FormField, FormFieldOption, FormSpec } from '@/types/form-spec'
import { normalizeParentValues } from '@/types/form-spec'

export interface FormOptionListItem {
  /** Stable stored value. Unique within the list (case-insensitive). */
  value: string
  label: string
  /** false = retired: hidden from new selections, still shown where saved. */
  active: boolean
  sort: number
  description?: string
  /** Cascading lists: stored values of the parent list (FormOptionList.parentListId)
   *  this item belongs to. Absent = offered under every parent value. */
  parentValues?: string[]
}

export interface FormOptionList {
  id: string
  /** Unique slug, for people and integrations to refer to the list by. */
  key: string
  name: string
  description: string | null
  items: FormOptionListItem[]
  /** The list whose values this list's items name in parentValues. */
  parentListId?: string | null
  createdAt: string
  updatedAt: string
  createdBy: string | null
  updatedBy: string | null
}

/** Where the Select Lists admin page lives (studio Setup group). */
export const OPTION_LISTS_PATH = '/workflow-management/select-lists'

export const OPTION_LIST_KEY_MAX = 64
export const OPTION_LIST_VALUE_MAX = 200
export const OPTION_LIST_LABEL_MAX = 200

/** Suffix shown after a retired value's label wherever it is still displayed. */
export const RETIRED_SUFFIX = ' (retired)'

/** A list key from free text: lowercase, digits, single hyphens. */
export function slugifyListKey(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, OPTION_LIST_KEY_MAX).replace(/-+$/, '')
}

const KEY_RE = /^[a-z0-9]+(?:[_-][a-z0-9]+)*$/

/** Why a list key cannot be saved, or null when it is fine. Mirrors the DB CHECK. */
export function listKeyError(key: string): string | null {
  if (!key) return 'Give the list a key.'
  if (key.length > OPTION_LIST_KEY_MAX) return `Keep the key to ${OPTION_LIST_KEY_MAX} characters.`
  if (!KEY_RE.test(key)) return 'Use lowercase letters, digits and single hyphens in the key (for example business-group).'
  return null
}

/**
 * Coerce stored items into a clean, defensive shape. Never throws: a row a
 * person or another writer garbled reads back as whatever is usable. Items
 * without a value are dropped; a missing label falls back to the value.
 */
export function normalizeOptionListItems(raw: unknown): FormOptionListItem[] {
  if (!Array.isArray(raw)) return []
  const out: FormOptionListItem[] = []
  raw.forEach((r, i) => {
    if (!r || typeof r !== 'object') return
    const o = r as Record<string, unknown>
    const value = typeof o.value === 'string' ? o.value.trim() : typeof o.value === 'number' ? String(o.value) : ''
    if (!value) return
    const label = typeof o.label === 'string' && o.label.trim() ? o.label.trim() : value
    const description = typeof o.description === 'string' && o.description.trim() ? o.description.trim() : undefined
    const parentValues = normalizeParentValues(o.parentValues)
    out.push({
      value,
      label,
      active: o.active !== false,
      sort: typeof o.sort === 'number' && Number.isFinite(o.sort) ? o.sort : i,
      ...(description ? { description } : {}),
      ...(parentValues ? { parentValues } : {}),
    })
  })
  return out
}

/** Items in display order: by sort, then by original position. */
export function sortedItems(items: FormOptionListItem[]): FormOptionListItem[] {
  return items.map((it, i) => ({ it, i })).sort((a, b) => a.it.sort - b.it.sort || a.i - b.i).map((x) => x.it)
}

/** Rewrite sort as 0..n-1 in the given order (after a reorder or an add). */
export function resequence(items: FormOptionListItem[]): FormOptionListItem[] {
  return items.map((it, i) => ({ ...it, sort: i }))
}

/**
 * Every problem that would stop a list's items from saving. Empty when the
 * items are valid. The database trigger enforces the same rules.
 */
export function validateOptionListItems(items: FormOptionListItem[]): string[] {
  const errors: string[] = []
  const seen = new Map<string, string>()
  items.forEach((it, i) => {
    const value = (it.value ?? '').trim()
    if (!value) { errors.push(`Row ${i + 1} has no value.`); return }
    if (value.length > OPTION_LIST_VALUE_MAX) errors.push(`The value "${value.slice(0, 40)}..." is longer than ${OPTION_LIST_VALUE_MAX} characters.`)
    if ((it.label ?? '').length > OPTION_LIST_LABEL_MAX) errors.push(`The label for "${value}" is longer than ${OPTION_LIST_LABEL_MAX} characters.`)
    const k = value.toLowerCase()
    const first = seen.get(k)
    if (first !== undefined) errors.push(`The value "${value}" appears more than once${first !== value ? ` (also as "${first}")` : ''}.`)
    else seen.set(k, value)
  })
  return errors
}

/** Parent values typed as text: separated by "|" or ";" (a value may hold commas). */
export function parseParentValues(text: string): string[] {
  return [...new Set(text.split(/[|;]/).map((p) => p.trim()).filter(Boolean))]
}

/**
 * Parse a bulk paste: one value per line, or "value,label" (a tab works too,
 * for a paste from a spreadsheet). Blank lines are skipped. A label containing
 * commas keeps them: only the first separator splits. A tab-separated line can
 * carry a third column of parent values ("BOM|DOC") for a child list.
 */
export function parseBulkPaste(text: string): { value: string; label: string; parentValues?: string[] }[] {
  return text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean).map((line) => {
    if (line.includes('\t')) {
      const [v = '', l = '', parents = ''] = line.split('\t')
      const parentValues = parseParentValues(parents)
      return { value: v.trim(), label: l.trim() || v.trim(), ...(parentValues.length ? { parentValues } : {}) }
    }
    const m = /^([^,]*),(.*)$/.exec(line)
    const value = (m ? m[1]! : line).trim()
    const label = (m ? m[2]! : '').trim() || value
    return { value, label }
  }).filter((x) => x.value !== '')
}

/**
 * Merge pasted rows into a list. A pasted value already in the list (any
 * case) updates that item's label and restores it if retired; a new value is
 * appended active. Returns the merged items plus what happened, for the toast.
 */
export function mergePastedItems(items: FormOptionListItem[], pasted: { value: string; label: string; parentValues?: string[] }[]): {
  items: FormOptionListItem[]
  added: number
  updated: number
} {
  const next = sortedItems(items).map((it) => ({ ...it }))
  const index = new Map(next.map((it, i) => [it.value.toLowerCase(), i]))
  let added = 0
  let updated = 0
  for (const p of pasted) {
    const at = index.get(p.value.toLowerCase())
    if (at !== undefined) {
      const cur = next[at]!
      const parentsChanged = p.parentValues !== undefined && (cur.parentValues ?? []).join('|') !== p.parentValues.join('|')
      if (cur.label !== p.label || !cur.active || parentsChanged) {
        next[at] = { ...cur, label: p.label, active: true, ...(p.parentValues !== undefined ? { parentValues: p.parentValues } : {}) }
        updated++
      }
      continue
    }
    index.set(p.value.toLowerCase(), next.length)
    next.push({ value: p.value, label: p.label, active: true, sort: next.length, ...(p.parentValues?.length ? { parentValues: p.parentValues } : {}) })
    added++
  }
  return { items: resequence(next), added, updated }
}

// ---------------------------------------------------------------------------
// Resolution: list -> field options
// ---------------------------------------------------------------------------

/** The select values a stored field value holds (a string or an array of them). */
export function selectedValues(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((v) => v != null && v !== '').map((v) => String(v))
  return value == null || value === '' ? [] : [String(value)]
}

function toOption(listId: string, it: FormOptionListItem): FormFieldOption {
  return {
    id: `${listId}:${it.value}`, value: it.value, label: it.label,
    ...(it.active ? {} : { retired: true }),
    ...(it.parentValues?.length ? { parentValues: it.parentValues } : {}),
  }
}

/** Parent values an item names that the parent list does not hold. */
export function unknownParentValues(item: Pick<FormOptionListItem, 'parentValues'>, parent: FormOptionList | null | undefined): string[] {
  if (!parent || !item.parentValues?.length) return []
  const known = new Set(parent.items.map((it) => it.value))
  return item.parentValues.filter((p) => !known.has(p))
}

/**
 * The options a list-backed field offers for picking: active items in sort
 * order, plus any retired item that is currently selected (marked retired, so
 * history shows correctly and the control does not silently drop it). A
 * selected value the list does not know at all is left out.
 */
export function selectableListOptions(list: FormOptionList, selected: string[]): FormFieldOption[] {
  const keep = new Set(selected)
  return sortedItems(list.items).filter((it) => it.active || keep.has(it.value)).map((it) => toOption(list.id, it))
}

/** Every option of a list, retired ones included and marked: for reading labels back. */
export function allListOptions(list: FormOptionList): FormFieldOption[] {
  return sortedItems(list.items).map((it) => toOption(list.id, it))
}

/** The label a person should see for an option (retired ones say so). */
export function optionDisplayLabel(o: Pick<FormFieldOption, 'label' | 'retired'>): string {
  return o.retired ? `${o.label}${RETIRED_SUFFIX}` : o.label
}

/** True when the field takes its options from a shared list (chosen or not yet). */
export function usesOptionList(field: Pick<FormField, 'type' | 'optionListId'>): boolean {
  return (field.type === 'single_select' || field.type === 'multi_select') && field.optionListId !== undefined
}

/** True when any field of the spec takes its options from a shared list. */
export function specUsesOptionLists(spec: Pick<FormSpec, 'fields'> | null | undefined): boolean {
  return (spec?.fields ?? []).some(usesOptionList)
}

/** How a list-backed field's options resolved, for the inline message. */
export type OptionListState = 'ok' | 'loading' | 'unchosen' | 'missing' | 'error'

export function optionListState(
  field: Pick<FormField, 'optionListId'>,
  lists: ReadonlyMap<string, FormOptionList> | null,
  status: { isLoading: boolean; isError: boolean },
): OptionListState {
  if (!field.optionListId) return 'unchosen'
  if (status.isError) return 'error'
  if (!lists) return status.isLoading ? 'loading' : 'error'
  return lists.has(field.optionListId) ? 'ok' : 'missing'
}

export const OPTION_LIST_STATE_MESSAGE: Record<Exclude<OptionListState, 'ok' | 'loading'>, string> = {
  unchosen: 'No shared list is chosen for this field yet. Ask the form owner to pick one.',
  missing: 'The shared list this field uses no longer exists. Ask the form owner to pick another list.',
  error: 'The shared list for this field could not be loaded. Reload the page to try again.',
}

/**
 * The spec with every list-backed select field's `options` filled from its
 * list, so validation, value formatting and exports work unchanged.
 *
 * `keepRetired` decides which retired values stay valid:
 *  - 'all': every retired value (read-only views, label lookups, exports).
 *  - a values object (the saved submission or draft being edited): only the
 *    retired values it already holds, so an existing pick still validates but
 *    a retired value cannot be newly chosen.
 *
 * A field whose list is missing (or not loaded) gets no options. A spec with
 * no list-backed field comes back as the same object.
 */
export function resolveSpecOptionLists(
  spec: FormSpec,
  lists: ReadonlyMap<string, FormOptionList> | null,
  keepRetired: 'all' | Record<string, unknown> = 'all',
): FormSpec {
  if (!specUsesOptionLists(spec)) return spec
  const saved = keepRetired === 'all' ? null : collectStrings(keepRetired)
  return {
    ...spec,
    fields: spec.fields.map((f) => {
      if (!usesOptionList(f)) return f
      const list = f.optionListId ? lists?.get(f.optionListId) : undefined
      if (!list) return { ...f, options: [] }
      const options = saved ? selectableListOptions(list, [...saved]) : allListOptions(list)
      return { ...f, options }
    }),
  }
}

/** Every string anywhere in a values object (top-level values and repeatable rows). */
function collectStrings(values: Record<string, unknown>): Set<string> {
  const out = new Set<string>()
  const walk = (v: unknown, depth: number) => {
    if (depth > 4 || v == null) return
    if (typeof v === 'string') { if (v) out.add(v); return }
    if (typeof v === 'number') { out.add(String(v)); return }
    if (Array.isArray(v)) { v.forEach((x) => walk(x, depth + 1)); return }
    if (typeof v === 'object') Object.values(v as Record<string, unknown>).forEach((x) => walk(x, depth + 1))
  }
  walk(values, 0)
  return out
}
